import { Box, Dialog, DialogContent, DialogProps, styled } from '@mui/material'
import ReaderHelpIcon, { ReaderHelpIconProps } from 'components/ReaderHelpIcon'
import { Properties } from 'csstype'

import Actions, { ActionsProps } from './Actions'
import Header, { HeaderProps } from './Header'
import LoadingCover from './LoadingCover'

type StyleProps = { align?: Properties['textAlign']; disableScroll?: boolean }

const MyDialogContent = styled(DialogContent, {
  shouldForwardProp: (prop) => prop !== 'disableScroll',
})<StyleProps>(({ align, disableScroll, theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  whiteSpace: 'pre-line',
  minWidth: theme.spacing(30),
  paddingBottom: theme.spacing(2),
  textAlign: align,
  '&>:first-of-type': {
    paddingTop: theme.spacing(0.5),
  },
  overflow: disableScroll ? 'hidden' : undefined,
}))

export type DialogBaseProps = {
  title?: string
  readerHelpIconProps?: ReaderHelpIconProps
  loading?: boolean
  loadingSize?: number

  disableScroll?: boolean
  backdropClosable?: boolean
  align?: Properties['textAlign']
  headerAlign?: HeaderProps['align']

  onCancel?: () => void
  onAction?: () => void
  onExtraAction?: () => void
  onClose?: () => void
  cancelText?: string
  actionText?: string
  extraActionText?: string
  cancelProps?: ActionsProps['cancelProps']
  actionProps?: ActionsProps['actionProps']
  extraActionProps?: ActionsProps['extraActionProps']
} & DialogProps

const DialogBase = ({
  loading = false,
  loadingSize = 50,
  align = 'center',
  headerAlign,
  open,
  title,
  readerHelpIconProps,
  disableScroll,
  backdropClosable,
  onClose,
  onCancel,
  cancelText,
  cancelProps,
  onAction,
  actionText,
  actionProps,
  onExtraAction,
  extraActionText,
  extraActionProps,
  children,
  ...props
}: DialogBaseProps) => {
  return (
    <Dialog
      onClose={backdropClosable ? onClose : undefined}
      open={open}
      {...props}
      maxWidth={false}
    >
      {title && (
        <Header onClose={onClose} align={headerAlign}>
          {title}
          {readerHelpIconProps && (
            <Box sx={{ display: 'inline-block', position: 'relative' }}>
              <Box sx={{ position: 'absolute', top: '-26px' }}>
                <ReaderHelpIcon
                  builderPath={readerHelpIconProps.builderPath}
                  concurPath={readerHelpIconProps.concurPath}
                  invoicePath={readerHelpIconProps.invoicePath}
                  orderFormPath={readerHelpIconProps.orderFormPath}
                />
              </Box>
            </Box>
          )}
        </Header>
      )}

      <MyDialogContent align={align} disableScroll={disableScroll}>
        {children}
      </MyDialogContent>

      <Actions
        onCancel={onCancel}
        cancelText={cancelText}
        cancelProps={cancelProps}
        onAction={onAction}
        actionText={actionText}
        actionProps={actionProps}
        onExtraAction={onExtraAction}
        extraActionText={extraActionText}
        extraActionProps={extraActionProps}
      />

      <LoadingCover loadingSize={loadingSize} open={loading} />
    </Dialog>
  )
}

export default DialogBase
